import { Container, Grid } from '@mui/material';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Command from '../../action/command';
import Events from '../../action/event';
import { HeaderRightButtonContext } from '../../layout/MainLayout';
import { paths } from '../../routes';
import { depotListSelector } from '../../selector/depotsSelector';
import {
    createdIssueReportIdSelector,
    issueReportCreateFailedSelector,
    issueReportCreatedSelector,
} from '../../selector/issueReportsSelector';
import { locationListSelector } from '../../selector/locationsSelector';
import IssueReportLoaderOverlay from './IssueReportLoaderOverlay';
import SelectField from '../../components/ui/SelectField';
import InputField from '../../components/ui/InputField';
import TextareaField from '../../components/ui/TextareaField';
import CustomButton from '../../components/CustomButton';
import { useAuth0 } from '../../react-auth0-spa';
import Logger from '../../common/logger';
import { RootState } from '../../store/store';
import { roomNounOf, serviceTypeLabel } from '../../utils/useCaseLabels';

interface IssueReportDialogProps {}

interface LocationState {
    locationId?: string;
    depotId?: string;
    door?: string | number;
    orderId?: string;
    service?: string;
    bookingNumber?: string;
    /** Where to go after a successful create instead of stepping back in
     *  history. Used by flows that need the new report's id — e.g. the
     *  goodwill-code form, which reopens with the report already linked. */
    returnTo?: string;
    /** Navigation state merged into the return navigation. */
    returnState?: Record<string, unknown>;
}

interface IssueReportCreationPayload {
    locationId: string;
    depotId: string;
    door: string;
    message: string;
    category: string;
    service: string;
    bookingNumber?: string;
}

const AddIssueReportPage = (props: IssueReportDialogProps) => {
    const { state } = useLocation() as { state: LocationState };
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth0();
    const issueReportCreated = useSelector(issueReportCreatedSelector);
    const createdIssueReportId = useSelector(createdIssueReportIdSelector);
    const issueReportCreateFailed = useSelector(issueReportCreateFailedSelector);
    const [loading, setLoading] = useState<boolean>(false);
    const { setButtonState } = useContext(HeaderRightButtonContext);
    const locations = useSelector(locationListSelector);
    const depots = useSelector(depotListSelector);
    const [issueReport, setIssueReport] = useState<IssueReportCreationPayload>({
        locationId: state?.locationId || '',
        depotId: state?.depotId || '',
        door: state?.door != null ? String(state.door) : '',
        message: '',
        category: '',
        service: state?.service || '',
        bookingNumber: state?.bookingNumber || '',
    });

    // Get allowed services from tenant config in authentication slice
    const allowedServices = useSelector((state: RootState) => state.authentication.config?.services || []);
    Logger('Tenant Allowed Services').log('Allowed services from config:', allowedServices);

    // Map service keys to descriptive names

    // "general" is always available, then tenant-specific services
    const serviceKeys = ['general', ...allowedServices.filter((s: string) => s !== 'general')];
    const serviceOptions = serviceKeys.map((key: string) => ({
        key,
        // Every service's name, hall included, from service_type_selector.<service>.
        label: serviceTypeLabel(t, key),
    }));
    Logger('IssueReportObject').log('IssueReport object:', { issueReport });
    // // Fetch locations from API (with services array)
    // const { data: locationsApiData } = useGetLocationListQuery('');
    // // Get the services array from the first location object in the response
    // const allowedServices = locationsApiData && locationsApiData.length > 0 ? locationsApiData[0].services : [];
    // Logger('Tenant Allowed Services').log('Allowed services:', allowedServices);
    const issueTypeOptions = [
        'general_questions',
        'locker_technical_issue',
        'locker_user_issue',
        'equipment_damaged_dirty',
        'locker_damaged_dirty',
        'emergency_person_danger',
        'payment_booking_issue',
        'problem_with_app',
        'other_not_listed',
        
    ].map((key) => ({
        text: t(`issue_type.${key}`),
        value: key,
    }));
    // Cabinet and locker are optional: plenty of problems don't concern a
    // specific locker at all (payment issues, app problems, general
    // questions), and locations without a station have no locker to pick.
    // Everything else stays required.
    const REQUIRED_FIELDS: (keyof IssueReportCreationPayload)[] = [
        'locationId',
        'category',
        'service',
        'message',
    ];
    const isSubmitDisabled = REQUIRED_FIELDS.some((field) => !issueReport[field]);

    const submitDamageReport = () => {
        setLoading(true);

        // Only send an asset entry when a locker was actually picked — an
        // entry with an empty depot and door 0 points at no real locker.
        const assets = issueReport.depotId && issueReport.door
            ? [{ door: Number(issueReport.door), depotId: issueReport.depotId }]
            : [];

        const payload: any = {
            message: issueReport.message,
            assets,
            locationId: issueReport.locationId,
            bookingNumber: issueReport.bookingNumber,
            issueType: issueReport.category,
            service: issueReport.service || '',
            profileInfo: {
                name: user.name,
                sub: user.sub,
                nickname: user.nickname,
                email: user.email,
                picture: user.picture,
            },
        };

        if (state?.orderId) {
            payload.orderId = state.orderId;
        }

        dispatch(Command.IssueReport.createIssueReport(payload));
    };
    const filterDepotsForLocation = (locationId: string) => {
        return depots.filter((depot) => depot.locationId === locationId);
    };

    const generateLockerOptions = (depotId: string) => {
        const depot = depots.find((depot) => depot.id === depotId);
        if (depot) {
            const lockers = Array.from({ length: depot.doorCount - 1 + 1 }, (_, i) => i + 1);
            return lockers.map((locker) => ({
                value: String(locker),
                text: `${t('issue_report.locker')} ${locker}`,
            }));
        }

        return [];
    };

    const updateHeaderRightButton = (status: boolean, route: string) => {
        setButtonState({
            isCancelButton: status,
            previousRoute: route,
        });
    };

    // One effect for both outcomes, and one `setLoading` writer: a second
    // effect driving the same setter is exactly the stacked-writer race the
    // engineering rules forbid.
    useEffect(() => {
        if (!issueReportCreated && !issueReportCreateFailed) {
            return;
        }
        setLoading(false);
        const failed = issueReportCreateFailed;
        const newReportId = createdIssueReportId;
        dispatch(Events.IssueReport.createdIssueReport({ success: false }));
        if (failed) {
            // Stay on the form with the agent's input intact — the error
            // snackbar says nothing was saved. Navigating away here is what
            // made a failed create look like a successful one.
            return;
        }
        updateHeaderRightButton(false, '');
        if (state?.returnTo) {
            // Hand the caller its own state back plus the new report's id,
            // so it can resume exactly where it left off (the goodwill
            // form reopens with this report selected).
            navigate(state.returnTo, {
                state: { ...(state.returnState ?? {}), createdIssueReportId: newReportId },
            });
            return;
        }
        navigate(-1);
    }, [issueReportCreated, issueReportCreateFailed]);

    const isInitialMount = useRef(true);

    useEffect(() => {
        // On initial mount, keep pre-filled values from navigation state
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (state?.depotId) return;
        }

        const depotsForLocation = filterDepotsForLocation(issueReport.locationId);
        if (depotsForLocation.length === 1) {
            setIssueReport((prev) => ({ ...prev, depotId: depotsForLocation[0].id, door: '' }));
        } else {
            setIssueReport((prev) => ({ ...prev, depotId: '', door: '' }));
        }
    }, [issueReport.locationId]);

    useLayoutEffect(() => {
        // A flow that sent the user here (goodwill code, …) owns where
        // "cancel" goes — without this the derived route below would point
        // back at this very page for entries under /issue-reports/add.
        if (state?.returnTo) {
            updateHeaderRightButton(true, state.returnTo);
        } else if (state) {
            const route = location.pathname;
            const parts = route.split('/').slice(0, 3);
            updateHeaderRightButton(true, parts.join('/'));
        } else {
            updateHeaderRightButton(true, paths.issueReports);
        }
    }, []);

    return (
        <>
            <Container maxWidth={false}>
                <div className={'py-6'}>
                    <PageHeader>
                        <PageHeadline>{t('issue_reports.issue_report')}</PageHeadline>
                    </PageHeader>
                </div>
                <div className={'bg-white rounded-lg shadow-sm p-5'}>
                    <Grid
                        container
                        rowSpacing={3}
                        columnSpacing={{ xs: 0, sm: 2 }}
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            display: 'flex',
                        }}
                    >
                        <Grid item xs={12} sm={6}>
                            <SelectField
                                options={locations.map((loc) => loc.name)}
                                value={locations.find((loc) => loc.id === issueReport.locationId)?.name ?? ''}
                                onChange={(name) => {
                                    const selected = locations.find((loc) => loc.name === name);
                                    if (selected) {
                                        setIssueReport({ ...issueReport, locationId: selected.id });
                                    }
                                }}
                                disabled={locations.length === 0}
                                label={t('common.location') + ' *'}
                                noOptions={t(`issue_report.no_${roomNounOf(issueReport.service)}s`)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SelectField
                                options={issueTypeOptions.map((opt) => opt.text)}
                                value={issueTypeOptions.find((opt) => opt.value === issueReport.category)?.text ?? ''}
                                onChange={(selectedText) => {
                                    const selected = issueTypeOptions.find((opt) => opt.text === selectedText);
                                    if (selected) {
                                        setIssueReport({ ...issueReport, category: selected.value });
                                    }
                                }}
                                label={t('issue_report.issue_type') + ' *'}
                                noOptions={t('issue_report.no_categories')}
                            />
                        </Grid>
                        {filterDepotsForLocation(issueReport.locationId).length > 1 && (
                            <Grid item xs={12} sm={12}>
                                <SelectField
                                    options={filterDepotsForLocation(issueReport.locationId).map((depot) => depot.name)}
                                    value={
                                        filterDepotsForLocation(issueReport.locationId)
                                            .find((depot) => depot.id === issueReport.depotId)?.name ?? ''
                                    }
                                    onChange={(name) => {
                                        const selected = filterDepotsForLocation(issueReport.locationId)
                                            .find((depot) => depot.name === name);
                                        if (selected) {
                                            setIssueReport({ ...issueReport, depotId: selected.id, door: '' });
                                        }
                                    }}
                                    disabled={!issueReport.locationId}
                                    label={t('issue_report.cabinet_name')}
                                    noOptions={t('issue_report.no_depots')}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <SelectField
                                options={generateLockerOptions(issueReport.depotId).map((opt) => opt.text)}
                                value={
                                    generateLockerOptions(issueReport.depotId)
                                        .find((opt) => opt.value === issueReport.door)?.text ?? ''
                                }
                                onChange={(selectedText) => {
                                    const selected = generateLockerOptions(issueReport.depotId)
                                        .find((opt) => opt.text === selectedText);
                                    if (selected) {
                                        setIssueReport({ ...issueReport, door: selected.value });
                                    }
                                }}
                                disabled={!issueReport.depotId}
                                label={t('issue_report.locker_number')}
                                noOptions={t('issue_report.no_lockers')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                label={t('issue_report.booking_id')}
                                value={issueReport.bookingNumber}
                                onChange={(value) => setIssueReport({ ...issueReport, bookingNumber: value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <SelectField
                                options={serviceOptions.map((opt) => opt.label)}
                                value={serviceOptions.find((opt) => opt.key === issueReport.service)?.label ?? ''}
                                onChange={(label) => {
                                    const selected = serviceOptions.find((opt) => opt.label === label);
                                    if (selected) setIssueReport((prev) => ({ ...prev, service: selected.key }));
                                }}
                                label={t('common.service') + ' *'}
                                noOptions={t('issue_report.no_services')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <TextareaField
                                rows={5}
                                label={t('issue_report.description') + ' *'}
                                value={issueReport.message}
                                onChange={(value) => setIssueReport({ ...issueReport, message: value })}
                                className={'mb-4'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <div className={'flex gap-2 justify-start'}>
                                <div>
                                    <CustomButton
                                        title={t('issue_report.send_issue_report')}
                                        onClick={submitDamageReport}
                                        disabled={isSubmitDisabled || loading}
                                        isFilled={true}
                                        fullWidth={false}
                                    />
                                </div>
                                <div>
                                    <CustomButton
                                        title={t('issue_report.cancel_issue_report')}
                                        onClick={() => navigate(state?.returnTo ?? paths.issueReports)}
                                        disabled={false}
                                        isFilled={false}
                                        fullWidth={false}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </div>
                <IssueReportLoaderOverlay open={loading} />
            </Container>
        </>
    );
};

export default AddIssueReportPage;
